import { IconLock } from "@tabler/icons-react";
import { lazy } from "react";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import { useSetUserInUserAccess } from "@common/features/Team";
import { useFetchMeData } from "@common/features/User";
import notify from "@common/helpers/NotificationManager";
import { __ } from "@common/helpers/i18n";
import { LoadingState } from "@app/elements/container";
import ErrorPageShell from "@app/elements/errors/ErrorPageShell";
import {
  getPrivateRouteDecision,
  isAdminRouteBootstrapReady,
} from "./authRoute";
import { getSafeRedirectLocation, getSafeRedirectPath } from "./safeRedirect";

const AccountOnboarding = lazy(() => import("@app/pages/dashboard/OnBoarding"));

export function PublicRoute({ children }: { children: JSX.Element }) {
  const hasUser = useFetchMeData((data) => !!data.user).data!;
  const history = useHistory();

  const match = useRouteMatch<{
    hash?: string;
  }>("/:page(login|signup|recovery|password-reset|otp)/:hash?");

  const setUserInUserAccess = useSetUserInUserAccess();

  const params = new URLSearchParams(history.location.search);

  const redirect = {
    ...getSafeRedirectLocation(params.get("redirect")),
    state: { from: history.location },
  };

  if (hasUser && match?.params.hash && match?.params.hash != "otp") {
    setUserInUserAccess.mutate(match.params.hash, {
      onSuccess: () =>
        notify.success(__("You have been successfully added to teammate.")),
    });
  }

  return !hasUser ? children : <Redirect to={redirect} />;
}

export function PrivateRoute({
  children,
  verify = true,
  admin = false,
  requiresAccount = false,
}: {
  children: JSX.Element;
  verify?: boolean;
  admin?: boolean;
  requiresAccount?: boolean;
}) {
  const meDataQuery = useFetchMeData(undefined, undefined, false);
  const user = meDataQuery.data?.user;
  const history = useHistory();

  const shouldWaitForAdminBootstrap =
    admin &&
    !meDataQuery.isError &&
    (!meDataQuery.data ||
      (!!user && !isAdminRouteBootstrapReady(meDataQuery.data)));

  if (shouldWaitForAdminBootstrap) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-6">
        <LoadingState size="page" surface="plain" />
      </div>
    );
  }

  const decision = getPrivateRouteDecision({
    user,
    verify,
    admin,
    mobileVerificationEnabled: import.meta.env.VITE_MOBILE_VERIFICATION == "1",
    emailVerificationEnabled: import.meta.env.VITE_EMAIL_VERIFICATION == "1",
  });

  if (decision.type === "forbidden") {
    return (
      <ErrorPageShell
        code="403"
        icon={IconLock}
        tone="warning"
        title={__("You do not have access to this section")}
        description={__(
          "Your account does not have permission to view this page. Ask the account owner to update your access if needed.",
        )}
        primaryAction="dashboard"
        standalone={false}
      />
    );
  }

  if (decision.type === "redirect") {
    const params = new URLSearchParams(history.location.search);
    const search =
      "redirect=" +
      encodeURIComponent(
        getSafeRedirectPath(
          params.get("redirect") ||
            history.location.pathname + history.location.search,
        ),
      );
    const redirect = {
      pathname: decision.pathname,
      search,
      state: { from: history.location },
    };
    return <Redirect to={redirect} />;
  }

  if (
    requiresAccount &&
    !admin &&
    meDataQuery.data?.masterUser?.account_count === 0
  ) {
    return <AccountOnboarding />;
  }

  return children;
}
